/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Locale segment SiteGenesis uses in its pipeline URLs. The B2C Apple Pay endpoints are
 * session-bound, not locale-bound, so the legacy default is always correct here.
 */
const LEGACY_LOCALE = 'default';

/** Placeholder element the B2C Apple Pay script swaps for its `.dw-apple-pay-button`. */
export const APPLE_PAY_TAG = 'isapplepay';

/** `__SYSTEM__ApplePay-*` pipelines exposed by the platform, keyed as the B2C script expects. */
const APPLE_PAY_ACTIONS = {
    cancel: 'Cancel',
    getRequest: 'GetRequest',
    onshippingcontactselected: 'ShippingContactSelected',
    onpaymentmethodselected: 'PaymentMethodSelected',
    onvalidatemerchant: 'ValidateMerchant',
    onpaymentauthorized: 'PaymentAuthorized',
    prepareBasket: 'PrepareBasket',
    onshippingmethodselected: 'ShippingMethodSelected',
} as const;

type ApplePayConfig = {
    action: Record<keyof typeof APPLE_PAY_ACTIONS, string>;
    inject: { directives: Array<{ css: string; query: string; action: string; copy: boolean }> };
};

declare global {
    interface Window {
        ApplePaySession?: { canMakePayments: () => boolean };
        dw?: { applepay?: ApplePayConfig };
    }
}

/**
 * Builds the same `dw.applepay` config the platform injects into SiteGenesis pages that
 * contain `<isapplepay>`. URLs are relative: in hybrid mode `/on/demandware.store/*` routes
 * to SiteGenesis on the same host, so the calls share the shopper's `dwsid` session.
 */
export function buildApplePayConfig(siteId: string): ApplePayConfig {
    const base = `/on/demandware.store/Sites-${siteId}-Site/${LEGACY_LOCALE}/__SYSTEM__ApplePay-`;
    const action = Object.fromEntries(
        Object.entries(APPLE_PAY_ACTIONS).map(([key, pipeline]) => [key, `${base}${pipeline}`])
    ) as ApplePayConfig['action'];

    return {
        action,
        inject: {
            directives: [{ css: 'dw-apple-pay-button', query: 'ISAPPLEPAY', action: 'replace', copy: true }],
        },
    };
}

export function getApplePayScriptUrl(siteId: string): string {
    return `/on/demandware.static/Sites-${siteId}-Site/-/${LEGACY_LOCALE}/internal/jscript/applepay.js`;
}

/** Cookie the platform script polls (every 200ms); a new value makes it re-fetch `GetRequest`. */
const UPDATE_REQUEST_COOKIE = 'dwapupreq';

/** Tells the B2C Apple Pay script the basket changed so it reloads the Apple Pay request/total. */
export function requestApplePayRefresh(): void {
    document.cookie = `${UPDATE_REQUEST_COOKIE}=${Date.now()}; path=/; secure; samesite=lax`;
}

/**
 * Key that changes whenever the Apple Pay request would: a different basket, item count or total.
 * Pass to `<ApplePayButton refreshKey>` on cart surfaces.
 */
export function getApplePayRefreshKey(
    basket?: {
        basketId?: string;
        orderTotal?: number;
        productTotal?: number;
        productItems?: Array<{ quantity?: number }>;
    } | null
): string | undefined {
    if (!basket) return undefined;
    const quantity = (basket.productItems ?? []).reduce((sum, item) => sum + (item.quantity ?? 0), 0);
    return [basket.basketId ?? '', basket.orderTotal ?? basket.productTotal ?? 0, quantity].join(':');
}
